import { Component, ViewChild, ElementRef, Input } from "@angular/core";
import { CommonModule, Location, NgOptimizedImage } from "@angular/common";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { MatListModule } from "@angular/material/list";
import { MatCardModule } from "@angular/material/card";
import { DataService } from "../../Services/data.service";
import { DialogService } from "../../Services/dialog.service";
import { ActivatedRoute, Router } from "@angular/router";
import { RouterLink } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { CommentsComponent } from "../comments/comments.component";
import {
  MyBoolean,
  OrderDetailsNew,
  ToothDataNew,
} from "../../models/order.models";
import { PropAComponent } from "../create-task-demo/prop-a/prop-a.component";
import { PropHComponent } from "../create-task-demo/prop-h/prop-h.component";
import { PropPComponent } from "../create-task-demo/prop-p/prop-p.component";
import { PropPFrameComponent } from "../create-task-demo/prop-p-frame/prop-p-frame.component";
import { PropCollarsComponent } from "../create-task-demo/prop-collars/prop-collars.component";
import { PropCutbackComponent } from "../create-task-demo/prop-cutback/prop-cutback.component";
import { PropPonticComponent } from "../create-task-demo/prop-pontic/prop-pontic.component";
import { PropVComponent } from "../create-task-demo/prop-v/prop-v.component";
import { MatRadioModule } from "@angular/material/radio";
import { firstValueFrom } from "rxjs";
import { MatBadgeModule } from "@angular/material/badge";
import { FilesComponent } from "../files/files.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FeedbackDialogComponent } from "../task-list/feedback-dialog/feedback-dialog.component";
import { ConfirmDialogComponent } from "../dialogs/confirm-dialog/confirm-dialog.component";
import { FormsModule } from "@angular/forms";
import jsPDF from "jspdf";
import * as JSZip from "jszip";
import * as THREE from "three";
import { STLLoader } from "three-stdlib";

import { ViewChildren, QueryList, AfterViewInit } from "@angular/core";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { FilesDialogComponent } from "../files-dialog/files-dialog.component";
import { DoctorReviewDialogComponent } from "../doctor-review-dialog/doctor-review-dialog.component";
import { MagnifierComponent } from "../magnifier/magnifier.component";
import { PdfExportService } from "../../Services/pdf-export.service";
import { HomeComponent } from "../home/home.component";
import { OrderReiewModelComponent } from "../order-reiew-model/order-reiew-model.component";
import { lastValueFrom } from "rxjs";

export interface RxDetails {
  procedureId: number;
  procedureType: string;
  teethList: string[];
}

export interface FileDetails {
  fileId: number;
  fileType: string;
  fileName: string;
}

export interface Comments {
  userId: string;
  comment: string;
  createTime: string;
}

@Component({
  selector: "app-task-details",
  standalone: true,
  imports: [
    RouterLink,
    HomeComponent,
    FormsModule,
    OrderReiewModelComponent,
    CommentsComponent,
    FilesComponent,
    MatTooltipModule,
    MatBadgeModule,
    MatRadioModule,
    PropAComponent,
    PropPComponent,
    PropHComponent,
    PropPonticComponent,
    PropVComponent,
    MatCardModule,
    CommonModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    NgOptimizedImage,
    MagnifierComponent,
    PropCollarsComponent,
    PropCutbackComponent,
    PropPFrameComponent,
    OrderReiewModelComponent,
  ],
  templateUrl: "./task-details.component.html",
  styleUrls: ["./task-details.component.css", "../../../style-custom.css"],
})
export class TaskDetailsComponent {
  @ViewChild("sectionToPdf", { static: false }) pdfContainer?: ElementRef;
  @ViewChild("sectionToPdf") sectionToPdf!: ElementRef<HTMLElement>;
  @Input() orderDetailsNew!: OrderDetailsNew;
  @Input() fileDetailsImagesContent!: string[];
  @Input() fromSummary: boolean = false;
  @ViewChild("pdfContent", { static: false }) pdfContent!: ElementRef;
  @ViewChild(OrderReiewModelComponent)
  orderReviewModal?: OrderReiewModelComponent;

  @ViewChild("stlContainer", { static: false }) stlContainer!: ElementRef;

  selectFile(file: any) {
    this.selectedFile = file;

    // Defer until next tick so the ViewChild stlContainer ref is available after Angular renders the STL panel
    setTimeout(() => {
      if (file.type === "stl" && this.stlContainer) {
        this.loadSTL(file.blob, this.stlContainer.nativeElement);
      }
    });
  }
  // ngAfterViewChecked() {
  //   if (this.showPreviewModal) {
  //     this.renderAllSTL();
  //   }
  // }
  // renderAllSTL() {
  //   const containers = this.stlContainers.toArray();

  //   let i = 0;

  //   this.previewData.forEach(file => {
  //     if (file.type === 'stl') {

  //       const container = containers[i];

  //       if (container) {
  //         this.loadSTL(file.blob, container.nativeElement);
  //       }

  //       i++;
  //     }
  //   });
  // }
  loadSTL(blob: Blob, container: HTMLElement) {
    const loader = new STLLoader();
    const reader = new FileReader();

    reader.onload = () => {
      const geometry = loader.parse(reader.result as ArrayBuffer);
      // computeVertexNormals is required for MeshStandardMaterial to calculate smooth lighting; center() prevents the model rendering off-screen
      geometry.computeVertexNormals();
      geometry.center();

      // ✅ Gray realistic material
      const material = new THREE.MeshStandardMaterial({
        color: 0x999999,
        metalness: 0.2,
        roughness: 0.8,
      });

      const mesh = new THREE.Mesh(geometry, material);

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf5f5f5); // light background
      scene.add(mesh);

      // ✅ Camera
      const camera = new THREE.PerspectiveCamera(
        75,
        container.clientWidth / 400,
        0.1,
        1000,
      );

      // ✅ Renderer
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(container.clientWidth, 400);

      container.innerHTML = "";
      container.appendChild(renderer.domElement);

      // ✅ Lights (VERY IMPORTANT for gray)
      const light1 = new THREE.DirectionalLight(0xffffff, 1);
      light1.position.set(1, 1, 1);
      scene.add(light1);

      const light2 = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(light2);

      // ✅ Orbit Controls (mouse interaction)
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enableZoom = true;
      controls.enablePan = true;

      // Bounding-box fit: offset camera Z by the diagonal size so the full model is visible regardless of scale
      const box = new THREE.Box3().setFromObject(mesh);
      const size = box.getSize(new THREE.Vector3()).length();
      const center = box.getCenter(new THREE.Vector3());

      camera.position.copy(center);
      camera.position.z += size;

      camera.lookAt(center);
      controls.target.copy(center);
      controls.update();

      // ❌ REMOVE auto rotation
      // mesh.rotation.y += 0.01;

      // ✅ Animate
      const animate = () => {
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };

      animate();
    };

    reader.readAsArrayBuffer(blob);
  }

  notifictionsState: any;
  isHovered: boolean = false;
  noPreviewAvailable: boolean = false;

  // get getBridgedToothData(){
  //   return this.orderDetailsNew.toothData.filter(data => data.type == 'Bridge');
  // }

  getToothColorForVisual(tooth: string): string {
    var color = "#ffffff";
    if (
      this.orderDetailsNew.toothData.some(
        (data) => data.teeth == Number(tooth) && data.subType == "Crown",
      )
    ) {
      color = "#397EAC";
    } else if (
      this.orderDetailsNew.toothData.some(
        (data) => data.teeth == Number(tooth) && data.subType == "Pontic",
      )
    ) {
      color = "#3939AC";
    } else if (
      this.orderDetailsNew.toothData.some(
        (data) => data.teeth == Number(tooth) && data.subType == "Inlay/Onlay",
      )
    ) {
      color = "#7E39AC";
    } else if (
      this.orderDetailsNew.toothData.some(
        (data) => data.teeth == Number(tooth) && data.subType == "Veneer",
      )
    ) {
      color = "#AC3995";
    }
    return color;
  }

  getTextColorForVisual(tooth: string): string {
    return this.orderDetailsNew.toothData.some(
      (data) => data.teeth == Number(tooth),
    )
      ? "#000000"
      : "#979797";
  }

  showVisualRectBridge(toothIdA: number, toothIdB: number): boolean {
    let isBridge = false;
    this.orderDetailsNew.toothData.forEach((data) => {
      if (data.teeth == toothIdA && data.teeth == toothIdB) {
        isBridge = true;
      }
    });
    return (
      this.showVisualRect(toothIdA) && this.showVisualRect(toothIdB) && isBridge
    );
  }

  showVisualRect(toothId: number): boolean {
    let returnValue = false;
    this.orderDetailsNew.toothData.forEach((data) => {
      if (data.teeth == toothId) {
        returnValue = true;
      }
    });
    return returnValue;
  }
  async saveSection(fileName: string) {
    this.dataService.updateProgressBarSubjectForSlider(true);
    try {
      await (document as any).fonts?.ready?.catch(() => {});
      await new Promise((r) => setTimeout(r, 0));

      await this.pdf.exportElement(this.sectionToPdf.nativeElement, {
        fileName: "order_" + fileName || "section.pdf",
        format: "a4",
        marginMm: 8,
        scale: 2,
      });
    } catch (err) {
      this.dialogService.handleError(
        err,
        "Failed to generate PDF. Please try again.",
      );
    } finally {
      this.dataService.updateProgressBarSubjectForSlider(false);
    }
  }

  get getPonticToothData(): ToothDataNew[] {
    return this.orderDetailsNew.toothData.filter((data) =>
      data.subType.includes("Pontic"),
    );
  }

  get anteriorTeethForAnatomy(): number[] {
    return this.dataService.anteriorTeethForAnatomy(this.orderDetailsNew);
  }

  get posteriorTeethForAnatomy(): number[] {
    return this.dataService.posteriorTeethForAnatomy(this.orderDetailsNew);
  }

  anteriorTeethForCrownOrPonticFrame(bridgedTeeth: number[]): number[] {
    return this.dataService.anteriorTeethForCrownOrPonticFrame(
      this.orderDetailsNew,
      bridgedTeeth,
    );
  }

  posteriorTeethForCrownOrPonticFrame(bridgedTeeth: number[]): number[] {
    return this.dataService.posteriorTeethForCrownOrPonticFrame(
      this.orderDetailsNew,
      bridgedTeeth,
    );
  }

  get anteriorTeeth(): number[] {
    return this.dataService.groupByAnteriorAndPosterior(
      this.orderDetailsNew.toothData.map((data) => data.teeth),
    ).anterior;
  }

  get posteriorTeeth(): number[] {
    return this.dataService.groupByAnteriorAndPosterior(
      this.orderDetailsNew.toothData.map((data) => data.teeth),
    ).posterior;
  }

  get anteriorTeethForCrownOrPontic(): number[] {
    return this.dataService.anteriorTeethForCrownOrPontic(this.orderDetailsNew);
  }

  get posteriorTeethForCrownOrPontic(): number[] {
    return this.dataService.posteriorTeethForCrownOrPontic(
      this.orderDetailsNew,
    );
  }

  get getAllToothCountFromOrderDetails(): number {
    return this.orderDetailsNew.toothData.reduce((acc, data) => acc + 1, 0);
  }
  get getPlanSubTitleFromTier(): string {
    let subTitle = "";
    switch (this.orderDetailsNew.tier) {
      case "silver":
        subTitle = "Essential Design";
        break;
      case "gold":
        subTitle = "Advanced Quality";
        break;
      case "platium":
        subTitle = "Design Elite";
        break;
      default:
        subTitle = "";
    }
    return subTitle;
  }

  getTeethByComma(teeth: number[]): string {
    return teeth.sort((a, b) => a - b).join(", ");
  }

  taskId: number = 0;
  taskDetails: any;
  status: string = "";
  showSliderFile: MyBoolean = { value: false };
  showSliderComments: MyBoolean = { value: false };
  currentPage: number = 0;
  pageSizes: number = 10;

  constructor(
    private router: Router,
    private home: HomeComponent,
    private pdf: PdfExportService,
    public dataService: DataService,
    private dialogService: DialogService,
    private route: ActivatedRoute,
    private location: Location,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
  ) {}

  get getBridgeToothData(): any[] {
    var bridgeMaterialData: any[] = [];

    this.orderDetailsNew.bridges.forEach((bridge) => {
      this.orderDetailsNew.toothData
        .filter((data) => data.teeth == bridge.bridgedTeeth[0])
        .forEach((data) => {
          bridgeMaterialData.push({
            teeth: bridge.bridgedTeeth.join(", "),
            material: data.props["Material"],
            minimalThickness: data.minimalThickness,
            cementGap: data.cementGap,
            marginGap: data.marginGap,
            horizontalBorder: data.horizontalBorder,
            radiusCompensation: data.radiusCompensation,
          });
        });
    });

    return bridgeMaterialData;
  }

  get getNonBridgeToothData(): ToothDataNew[] {
    return this.orderDetailsNew.toothData.filter(
      (data) => !data.bridgeConnectionType,
    );
  }

  get getToothDataForVisualisation(): ToothDataNew[] {
    var toothDataToReturn: ToothDataNew[] = [];
    // toothDataToReturn.push(...this.getBridgeToothData);
    // toothDataToReturn.push(...this.orderDetailsNew.toothData.filter(data => !data.bridgeConnectionType && !this.getBridgeToothData.some(bts => this.dataService.checkTwoSetIntersect(data.teeth, bts.teeth))));
    return toothDataToReturn;
  }

  updateFileCounts(filesData: any) {
    this.fileDetailsScan = filesData.fileDetailsScan;
    this.fileDetailsImages = filesData.fileDetailsImages;
    this.fileDetailsImagesContent = filesData.fileDetailsImagesContent;
  }

  notificationType: string = "";
  showOrderReviewModal: boolean = false;
  selectedStatuses: string[] = [];
  selectedOptions: string[] = [];
  isShowPreview: boolean = false;
  countPreview: number = 1;

  userName = "";
  taskCreatedBy = "";
  loggedInUserRole = "";

  ngOnInit() {
    // this.dataService.updateTitle("Task Details");

    this.userName = sessionStorage.getItem("userId") || "";

    // queryParamMap gives typed access to params; a second queryParams subscription below restores pagination/filter state
    this.route.queryParamMap.subscribe((params) => {
      this.taskId = Number(params.get("taskId"));
      this.notifictionsState = String(params.get("status"));
      this.notificationType = params.get("notificationType") || "";
      // this.orderDetailsNew = {} as OrderDetailsNew;
      // this.taskDetails = {};

      if (this.taskId > 0 && !this.orderDetailsNew) {
        this.dataService.updateProgressBarSubject(true);

        this.dataService.get("/tasks/" + this.taskId).subscribe({
          next: (response) => {
            this.taskDetails = response;
            this.orderDetailsNew = JSON.parse(response.jsonData);
            this.taskCreatedBy = response.crtBy;
            this.updateNotifications();
            this.getFileDetails();
            this.getComments();

            this.loggedInUserRole = sessionStorage.getItem("role") || "";
          },
          error: (err) => {
            this.dialogService.handleError(
              err,
              "Failed to get Task Details, Please try again.",
            );
            this.router.navigate(["task-list/all-tasks"]);
          },
        });
      }
    });
    this.route.queryParams.subscribe((params) => {
      this.currentPage = +params["page"] || 0;
      this.pageSizes = +params["pageSize"] || 10;
      const statusParam = params["status"];
      this.selectedStatuses = statusParam ? statusParam.split(",") : [];
      const storedFilters = sessionStorage.getItem("selectedTaskFilters");

      if (storedFilters) {
        const filters = JSON.parse(storedFilters);

        // restore filters back into service
        this.dataService.setSelectedTaskFilters(filters);
      }
    });

    if (this.taskId > 0 && !this.orderDetailsNew) {
      this.dataService.updateProgressBarSubject(true);
      this.dataService.get("/tasks/" + this.taskId).subscribe({
        next: (response) => {
          this.taskDetails = response;
          this.orderDetailsNew = JSON.parse(response.jsonData);
          this.updateNotifications();
          this.getFileDetails();
          this.getComments();
          // this.getFileCountsFromBackend();
          // this.getCommentsCountsFromBackend();
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to get Task Details, Please try again.",
          );
          this.router.navigate(["task-list/all-tasks"]);
        },
      });
    }
    if (this.countPreview == 1) this.isShowPreview = true;
  }

  updateNotifications() {
    if (
      this.notificationType != "comment" &&
      this.notificationType != "task delivered"
    ) {
      return;
    }

    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .post(
        "/common/notifications/" +
          this.taskId +
          "/inactive?type=" +
          this.notificationType,
        {},
      )
      .subscribe({
        next: (response) => {
          // this.showSliderComments.value = true;
          if (this.notificationType == "comment") {
            this.openCommentModal();
          }
        },
        error: (err) => {
          // this.dialogService.handleError(err, 'Failed to Complete task, Please try again.');
        },
      });
  }

  async populateImagesContent(fileId: number) {
    try {
      const response = await firstValueFrom(
        this.dataService.get("/tasks/" + this.taskId + "/files/" + fileId, {
          responseType: "blob",
        }),
      );
      this.fileDetailsImagesContent.push(
        URL.createObjectURL(
          new Blob([response], { type: "application/octet-stream" }),
        ),
      );
    } catch (err) {
      this.dialogService.handleError(
        err,
        "Failed to get File Contents, Please try again.",
      );
    }
  }

  fileDetailsScan: FileDetails[] = [];
  fileDetailsImages: FileDetails[] = [];
  // fileDetailsImagesContent: string[] = [];
  fileDetailsOrderForm: FileDetails[] = [];
  fileDetailsDesign: FileDetails[] = [];
  fileDetailsVisual: FileDetails[] = [];
  fileDetailsCorrectedDesign: FileDetails[] = [];
  fileDetailsCorrected3dVisualisation: FileDetails[] = [];
  private orderModalInitialized = false;
  previewFiles: FileDetails[] = [];
  isFilesLoaded = false;

  getFileDetails() {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .getDataAsList("/tasks/" + this.taskId + "/files")
      .subscribe({
        next: (response: FileDetails[]) => {
          this.fileDetails = response;

          this.fileDetailsScan = [];
          this.fileDetailsDesign = [];
          this.fileDetailsVisual = [];
          this.fileDetailsImages = [];
          this.fileDetailsImagesContent = [];
          this.fileDetailsCorrectedDesign = [];
          this.fileDetailsCorrected3dVisualisation = [];

          response.forEach((file) => {
            if (file.fileType == "scan") {
              this.fileDetailsScan.push(file);
            } else if (file.fileType == "design") {
              this.fileDetailsDesign.push(file);
            } else if (file.fileType == "visual") {
              this.fileDetailsVisual.push(file);
            } else if (file.fileType == "images") {
              this.fileDetailsImages.push(file);
              this.populateImagesContent(file.fileId);
            } else if (file.fileType == "correcteddesign") {
              this.fileDetailsCorrectedDesign.push(file);
            } else if (file.fileType == "correctedvisual") {
              this.fileDetailsCorrected3dVisualisation.push(file);
            } else {
              this.fileDetailsDesign.push(file);
            }
          });
          // countPreview guard ensures the auto-preview zip is triggered exactly once per page load, not on every file list reload
          if (this.isShowPreview && this.countPreview == 1) {
            this.fileDetailsVisual = response.filter(
              (file) =>
                file.fileType === "visual" ||
                file.fileType === "correctedvisual",
            );

            if (this.fileDetailsVisual.length > 0) {
              this.previewData = []; // reset once
              this.dataService.updateProgressBarSubject(true);
              this.previewZip(this.fileDetailsVisual[0]);
              this.dataService.updateProgressBarSubject(false);
            }

            this.isShowPreview = false;
            this.countPreview++;
          }

          // if(this.taskDetails.status == 'PF' && this.dataService.role == 'doctor' && this.notificationType != 'comment'){
          //   this.openFeedbackDialog();
          // }
          if (
            this.taskDetails.status === "PF" &&
            this.dataService.role === "doctor"
          ) {
            this.showOrderReviewModal = true;
          } else {
          }

          this.dataService.updateProgressBarSubject(false);
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to get Files for this Task, Please try again.",
          );
        },
      });
  }

  @ViewChild("orderReviewModal")
  set modal(content: OrderReiewModelComponent | undefined) {
    // Only trigger if content exists AND it hasn't been initialized for this specific open action
    if (content && !this.orderModalInitialized) {
      this.orderModalInitialized = true;
      this.orderReviewModal = content;

      // Use setTimeout to move the call outside the current change detection cycle
      setTimeout(() => {
        this.orderReviewModal?.getFileDetails(this.taskId);
      });
    }
  }

  // When you close the modal in the parent, reset the flag
  closeOrderModal() {
    this.showOrderReviewModal = false;
    this.orderModalInitialized = false; // Important: allows reopening later
  }

  selectedTaskId!: number;

  openOrderReview(taskId: number) {
    this.selectedTaskId = taskId;
    this.showOrderReviewModal = true;
  }

  closeOrderReviewModal() {
    this.showOrderReviewModal = false;
  }

  openFeedbackDialog() {
    this.dialog.open(DoctorReviewDialogComponent, {
      width: "60%",
      height: "80%",
      data: {
        taskId: this.orderDetailsNew.taskId,
        refNum: this.orderDetailsNew.refNum,
        fileDetailsDesign: this.fileDetailsDesign,
        fileDetailsVisual: this.fileDetailsVisual,
        fileDetailsCorrectedDesign: this.fileDetailsCorrectedDesign,
        fileDetailsCorrectedVisual: this.fileDetailsCorrected3dVisualisation,
        taskStatus: this.taskDetails.status,
        feedbackId: this.taskDetails.feedbackId,
        cancelReason: this.taskDetails.cancelReason,
        remainingCorrections: this.taskDetails.remainingCorrections,
        tier: this.taskDetails.tier,
      },
    });
  }

  commentCountsNew: any = {};
  commentCountsTotal: any = {};

  getCommentsCountsFromBackend() {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .getDataAsList(
        "/tasks/comments/" +
          this.statusMapForBackendCall.get(this.taskDetails.status),
      )
      .subscribe({
        next: (response) => {
          response.forEach((task) => {
            this.commentCountsNew[task.taskId] = task.newCount;
            this.commentCountsTotal[task.taskId] = task.totalCount;
          });
          this.dataService.updateProgressBarSubject(false);
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to get Comment Counts, Please try again.",
          );
        },
      });
  }

  openCommentModal() {
    this.dialog
      .open(CommentsComponent, {
        maxWidth: "70vw",
        height: "80vh",
        data: {
          taskId: this.orderDetailsNew.taskId,
          refNum: this.orderDetailsNew.refNum,
        },
      })
      .afterClosed()
      .subscribe();
  }

  statusMap = new Map<string, string>([
    ["SU", "Created"],
    ["IP", "In Progress"],
    ["OH", "On Hold"],
    ["PF", "Pending Feedback"],
    ["IC", "In Correction"],
    ["CO", "Complete"],
    ["PP", "Pending Payment"],
  ]);

  fileCountsNew: any = {};
  fileCountsDesign: any = {};
  fileCountsScan: any = {};
  fileCountsVisual: any = {};
  fileCountsDesignImages: any = {};
  fileCountsOrderForm: any = {};
  fileCountsTotal: any = {};

  showElements: boolean = false;

  downloadFile(fileId: number, fileName: string) {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .get("/tasks/" + this.taskId + "/files/" + fileId, {
        responseType: "blob",
      })
      .subscribe({
        next: (response) => {
          const blob = new Blob([response], {
            type: "application/octet-stream",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          this.dataService.updateProgressBarSubject(false);
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to download the file, Please try again.",
          );
        },
      });
  }
  openFilesModal() {
    this.dialog
      .open(FilesDialogComponent, {
        width: "60%",
        height: "80%",
        data: {
          taskId: this.orderDetailsNew.taskId,
          refNum: this.orderDetailsNew.refNum,
          taskStatus: this.taskDetails.status,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        this.fileDetailsScan = result.fileDetailsScan;
        this.fileDetailsImages = result.fileDetailsImages;
        this.fileDetailsImagesContent = result.fileDetailsImagesContent;
      });
  }

  openZipFile(item: any) {
    const fileName = item.name.toLowerCase();

    item.entry.async("blob").then((blob: Blob) => {
      // ✅ HTML preview
      if (fileName.endsWith(".html")) {
        const url = URL.createObjectURL(
          new Blob([blob], { type: "text/html" }),
        );
        window.open(url);
      }

      // ❌ Unsupported → download instead
      else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = item.name;
        a.click();
      }
    });
  }
  openFilesModalForDownload(fileName: any) {
    const dialogRef = this.dialog.open(FilesComponent, {
      width: "60%",
      height: "80%",
      data: {
        taskId: this.orderDetailsNew.taskId,
        refNum: this.orderDetailsNew.refNum,
        onDownload: () => this.saveSection(fileName),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.fileDetailsScan = result.fileDetailsScan;
      this.fileDetailsImages = result.fileDetailsImages;
      this.fileDetailsImagesContent = result.fileDetailsImagesContent;
    });
  }

  getFileCountsFromBackend() {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .getDataAsList(
        "/tasks/files/" +
          this.statusMapForBackendCall.get(this.taskDetails.status),
      )
      .subscribe({
        next: (response) => {
          this.fileCountsDesign = {};
          this.fileCountsScan = {};
          this.fileCountsNew = {};
          this.fileCountsTotal = {};
          response.forEach((task) => {
            this.fileCountsDesign[task.taskId] = task.designCount;
            this.fileCountsScan[task.taskId] = task.scanCount;
            this.fileCountsVisual[task.taskId] = task.visualCount;
            this.fileCountsDesignImages[task.taskId] = task.designImagesCount;
            this.fileCountsOrderForm[task.taskId] = task.orderformCount;
            this.fileCountsNew[task.taskId] = task.newCount;
            this.fileCountsTotal[task.taskId] =
              task.designCount +
              task.scanCount +
              task.visualCount +
              task.designImagesCount +
              task.orderformCount;
          });
          // this.dataService.updateProgressBarSubject(false);
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to get File Counts, Please try again.",
          );
        },
      });
  }

  // title: any = { "dashboard": "Dashboard", 'in-progress': 'In Progress Tasks', my: 'My Tasks', complete: 'Completed Tasks', 'pending-payment': 'Pending Payment' };
  // statusMap = new Map<string, string>([
  //   ['SU', 'Created'],
  //   ['IP', 'In Progress'],
  //   ['OH', 'On Hold'],
  //   ['PF', 'Pending Feedback'],
  //   ['CO', 'Complete'],
  //   ['PP', 'Pending Payment']
  // ]);

  statusMapForBackendCall = new Map<string, string>([
    ["SU", "Created"],
    ["IP", "in-progress"],
    ["OH", "in-progress"],
    ["PF", "in-progress"],
    ["CO", "complete"],
    ["PP", "pending-payment"],
  ]);

  feedbackMap = new Map<number, string>([
    [4, "Approved"],
    [3, "Optimized"],
    [2, "Approved with Corrections"],
    [1, "Rejected"],
  ]);

  openFeedbackModal(taskId: number, refNum: string) {
    this.dialog
      .open(FeedbackDialogComponent, {
        width: "60%",
        height: "80%",
        data: { taskId: taskId, refNum: refNum, feedbackMap: this.feedbackMap },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.ngOnInit();

          this.dialogService.openMessageDialog(
            "Feedback Complete",
            "Feedback captured completely.",
          );
        }
      });
  }

  updateTask(taskId: any, action: string) {
    this.home.updateTask(taskId, action);
    this.router.navigate(["task-list/all-tasks"]);
  }

  // completeTask(taskId: number, refNum: string) {
  //   this.dialog.open(ConfirmDialogComponent, { width: '25%', data: { title: 'Confirm Task Completion?', lines: ['Case / Patient: ' + refNum, 'Task Id: ' + taskId] } }).afterClosed().subscribe(result => {
  //     if (!result) {
  //       return;
  //     }

  //     this.dataService.updateProgressBarSubject(true);
  //     this.dataService.put('/tasks/' + taskId + '/complete', {}).subscribe({
  //       next: (response) => {
  //         this.ngOnInit();
  //         this.dataService.updateProgressBarSubject(false);
  //         this.dialogService.openMessageDialog('Task Complete', 'Task compelted successfully.');
  //       },
  //       error: (err) => {
  //         this.dialogService.handleError(err, 'Failed to Complete task, Please try again.');
  //       }
  //     });
  //   });
  // }

  selectedTeeth: string[] = [];
  rxDetails: RxDetails[] = [];
  fileDetails: FileDetails[] = [];
  comments: Comments[] = [];

  selectedItem: string = "";

  // constructor(private dataService: DataService, private dialogService: DialogService, private route: ActivatedRoute, private location: Location, private dialog: MatDialog){}

  // isDoctor(){
  //   return this.dataService.getRole() == 'ROLE_doctor';
  // }

  // ngOnInit(){
  //   // this.isDoctor = this.dataService.getRole() == 'ROLE_doctor';

  //   this.route.queryParamMap.subscribe(params => {
  //     this.taskId = Number(params.get('taskId'));
  //   });

  //   this.dataService.updateProgressBarSubject(true);
  //   this.dataService.get('/tasks/'+this.taskId).subscribe({
  //     next: (response) => {
  //       this.taskDetails = response;
  //       this.getRxDetails();
  //       this.getFileDetails();
  //       this.getComments();
  //     },
  //     error: (err) => {
  //       this.dialogService.handleError(err, 'Failed to get Task Details, Please try again.');
  //     }
  //   });
  // }

  postComment(comment: string) {
    if (comment == undefined || comment == "") {
      return;
    }
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .post("/tasks/" + this.taskId + "/comments", comment)
      .subscribe({
        next: (response) => {
          this.getComments();
          this.dataService.updateProgressBarSubject(false);
        },
        error: (err) => {
          console.log({ err });
          this.dialogService.handleError(
            err,
            "Failed to post comment, Please try again.",
          );
        },
      });
  }

  getComments() {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .getDataAsList("/tasks/" + this.taskId + "/comments")
      .subscribe({
        next: (response) => {
          this.comments = response;
          this.dataService.updateProgressBarSubject(false);
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to get Comments for this Task, Please try again.",
          );
        },
      });
  }
  getFileBlob(fileId: number) {
    return this.dataService.get("/tasks/" + this.taskId + "/files/" + fileId, {
      responseType: "blob",
    });
  }

  previewData: {
    name: string;
    type: string;
    blob: Blob;
    url: string | SafeResourceUrl;
  }[] = [];
  zipContents: any[] = [];
  showPreviewModal = false;
  selectedFile: any = null;
  isPreviewLoading = false;
  async previewZip(file: any) {
    this.showElements = true;
    this.isPreviewLoading = true;
    this.previewData = [];
    this.noPreviewAvailable = false;

    this.getFileBlob(file.fileId).subscribe({
      next: async (response: Blob) => {
        const fileName = file.fileName.toLowerCase();
        const url = URL.createObjectURL(response);

        //CASE 1: ZIP
        if (fileName.endsWith(".zip")) {
          const zip = await JSZip.loadAsync(response);
          const entries = Object.values(zip.files);

          let found = false;

          for (let entry of entries) {
            if (entry.dir) continue;

            const name = entry.name.toLowerCase();
            const type = this.getPreviewType(name);

            if (type !== "unknown") {
              found = true;

              const blob = await entry.async("blob");
              const rawUrl = URL.createObjectURL(blob);

              const safeUrl = this.getSafeUrl(rawUrl, type);

              this.previewData.push({
                name: entry.name,
                type,
                blob,
                url: safeUrl,
              });
            }
          }

          if (!found) this.noPreviewAvailable = true;
        }

        //CASE 2: DIRECT FILE
        else {
          const type = this.getPreviewType(file.fileName);

          if (type !== "unknown") {
            const rawUrl = URL.createObjectURL(response);
            const safeUrl = this.getSafeUrl(rawUrl, type);

            this.previewData = [
              {
                name: file.fileName,
                type,
                blob: response,
                url: safeUrl,
              },
            ];
          } else {
            this.noPreviewAvailable = true;
          }
        }

        this.isPreviewLoading = false;
        this.showElements = false;
      },

      error: () => {
        this.isPreviewLoading = false;
        this.showElements = false;
      },
    });
  }
  getSafeUrl(url: string, type: string): SafeResourceUrl | string {
    if (type === "pdf") {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        url + "#toolbar=1&navpanes=0&zoom=page-width",
      );
    }

    if (type === "html") {
      return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    return url;
  }

  getPreviewType(fileName: string): string {
    const name = fileName.toLowerCase();

    if (name.endsWith(".stl")) return "stl";

    if (
      name.endsWith(".png") ||
      name.endsWith(".jpg") ||
      name.endsWith(".jpeg") ||
      name.endsWith(".webp")
    )
      return "image";

    if (name.endsWith(".html")) return "html";

    return "unknown";
  }
  openPreviewFromBlob(file: any) {
    this.selectedFileType = file.type;

    if (file.type === "image") {
      this.previewUrl = file.url;
    } else if (file.type === "html") {
      window.open(file.url, "_blank");
      return;
    } else if (file.type === "stl") {
      setTimeout(() => {
        this.loadSTL(file.blob, this.stlContainer.nativeElement);
      });
    }

    this.showPreviewModal = true;
  }
  selectedFileType = "";
  previewUrl: any = null;
  safePdfUrl!: SafeResourceUrl;
  pdfCache: Map<number, string> = new Map();
  currentPdfUrl: string | null = null;
  openPreview(file: any) {
    this.previewUrl = null;
    this.selectedFile = null;

    this.showPreviewModal = true;

    const fileName = file.fileName.toLowerCase();

    // PDF → call different API

    if (fileName.endsWith(".pdf")) {
      this.isPreviewLoading = true;
      this.selectedFileType = "pdf";
      this.showPreviewModal = true;

      // 1. Use cache (instant open if already loaded)
      if (this.pdfCache.has(file.fileId)) {
        const cachedUrl = this.pdfCache.get(file.fileId)!;

        this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          cachedUrl + "#toolbar=1&navpanes=0&view=FitH",
        );

        this.isPreviewLoading = false;
        return;
      }

      // ✅ 2. API call only if not cached
      this.getPdfBlob(this.taskId, file.fileId).subscribe((blob: Blob) => {
        // ✅ 3. Revoke previous URL (memory cleanup)
        if (this.currentPdfUrl) {
          URL.revokeObjectURL(this.currentPdfUrl);
        }

        // ✅ 4. Create ONLY ONE URL
        const url = URL.createObjectURL(blob);
        this.currentPdfUrl = url;

        // ✅ 5. Store in cache
        this.pdfCache.set(file.fileId, url);

        // ✅ 6. Use optimized viewer params
        this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          url + "#toolbar=1&navpanes=0&view=FitH",
        );

        this.isPreviewLoading = false;
      });

      return;
    }

    //OTHER FILES → normal API
    this.getFileBlob(file.fileId).subscribe((blob: Blob) => {
      // IMAGE
      if (
        fileName.endsWith(".png") ||
        fileName.endsWith(".jpg") ||
        fileName.endsWith(".jpeg")
      ) {
        this.selectedFileType = "image";
        this.previewUrl = URL.createObjectURL(blob);
      }

      // STL
      else if (fileName.endsWith(".stl")) {
        this.selectedFileType = "stl";

        setTimeout(() => {
          this.loadSTL(blob, this.stlContainer.nativeElement);
        });
      }

      // ZIP
      else if (fileName.endsWith(".zip")) {
        this.previewZip(file);
        return;
      } else {
        alert("Preview not available");
        return;
      }

      this.showPreviewModal = true;
    });
  }

  getPdfBlob(taskId: number, fileId: number) {
    return this.dataService.get(`/tasks/${taskId}/pdf/application/${fileId}`, {
      responseType: "blob",
    });
  }
  downloadPreviewFile(file: any) {
    const a = document.createElement("a");
    a.href = file.url || URL.createObjectURL(file.blob);
    a.download = file.name;
    a.click();

    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }
  closePreview() {
    this.showPreviewModal = false;
    this.previewUrl = null;

    // CLEAR OLD DATA
    this.previewUrl = null;
    this.selectedFile = null;
  }
  getFileIcon(type: string): string {
    switch (type) {
      case "stl":
        return "view_in_ar";
      case "image":
        return "image";
      case "pdf":
        return "picture_as_pdf";
      default:
        return "insert_drive_file";
    }
  }
  getFileType(name: string) {
    name = name.toLowerCase();

    if (name.endsWith(".stl")) return "stl";
    if (
      name.endsWith(".png") ||
      name.endsWith(".jpg") ||
      name.endsWith(".jpeg")
    )
      return "image";
    if (name.endsWith(".pdf")) return "pdf";
    if (name.endsWith(".html")) return "html";

    return "other";
  }

  getMimeType(name: string) {
    if (name.endsWith(".html")) return "text/html";
    if (name.endsWith(".png")) return "image/png";
    if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
    if (name.endsWith(".pdf")) return "application/pdf";
    return "application/octet-stream";
  }
  // downloadAllFiles(){
  //   this.dataService.updateProgressBarSubject(true);
  //   this.dataService.get('/tasks/'+this.taskId+'/files/all', { responseType: 'blob' }).subscribe({
  //     next: (response) => {
  //       const blob = new Blob([response], { type: 'application/octet-stream' });

  //       const fileName =  this.dataService.get('/tasks/'+this.taskId+'/ref-num').subscribe({
  //         next: (refNumResponse) => {
  //           return refNumResponse;
  //         },
  //         error: (err) => {
  //           return 'files';
  //         }
  //       });

  //       const url = window.URL.createObjectURL(blob);
  //       const a = document.createElement('a');
  //       a.href = url;
  //       a.download = fileName+'-'+this.taskId+'.zip';
  //       document.body.appendChild(a);
  //       a.click();
  //       a.remove();
  //       window.URL.revokeObjectURL(url);
  //       this.dataService.updateProgressBarSubject(false);
  //     },
  //     error: (err) => {
  //       this.dialogService.handleError(err, 'Failed to download all files, Please try again.');
  //     }
  //   });
  // }
  downloadAllFiles() {
    this.dataService.updateProgressBarSubject(true);

    const fileBlob$ = this.dataService.get(`/tasks/${this.taskId}/files/all`, {
      responseType: "blob",
    });

    const refNum$ = this.dataService
      .get(`/tasks/${this.taskId}/ref-num`, {
        responseType: "text",
      })
      .pipe(catchError(() => of("files")));

    forkJoin([fileBlob$, refNum$]).subscribe({
      next: ([response, refNum]) => {
        const blob =
          response instanceof Blob
            ? response
            : new Blob([response as any], { type: "application/zip" });

        if (blob.size === 0) {
          this.dataService.updateProgressBarSubject(false);
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        [];
        a.href = url;
        a.download = `${refNum}-${this.taskId}.zip`;
        document.body.appendChild(a);
        a.click();

        a.remove();
        window.URL.revokeObjectURL(url);
        this.dataService.updateProgressBarSubject(false);
      },
      error: (err) => {
        this.dataService.updateProgressBarSubject(false);
        this.dialogService.handleError(err, "Failed to download.");
      },
    });
  }

  getRxDetails() {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .getDataAsList("/tasks/" + this.taskId + "/procs")
      .subscribe({
        next: (response) => {
          this.rxDetails = response;
          this.dataService.updateProgressBarSubject(false);
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to get Rx Details for this Task, Please try again.",
          );
        },
      });
  }

  addFileDetails(event: any) {
    var fileList: FileList = event.target.files;
    if (fileList.length == 0) {
      this.dialogService.openMessageDialog("Error", "Select a file to Upload.");
    } else {
      Array.from(fileList).forEach((file) => this.uploadSingleFile(file));
    }
  }

  uploadSingleFile(file: File) {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .postFile("/tasks/" + this.taskId + "/files", file)
      .subscribe({
        next: (response) => {
          this.getFileDetails();
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to Upload File, Please try again.",
          );
        },
      });
  }

  removeFileDetails(fileId: number) {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .delete("/tasks/" + this.taskId + "/files/" + fileId)
      .subscribe({
        next: (response) => {
          this.getFileDetails();
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to Remove File, Please try again.",
          );
        },
      });
  }
  public waitUntilPdfReady(): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        if (this.pdfContainer?.nativeElement) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  generatePDF(id: string): Promise<Blob> {
    // Minimal PDF generation fallback so the method always returns a Blob promise.
    // This produces a small PDF containing the provided id; replace with html2canvas/html rendering if needed.
    return new Promise((resolve, reject) => {
      try {
        const pdf = new jsPDF("p", "mm", "a4");
        pdf.setFontSize(12);
        pdf.text("Export: " + id, 10, 10);
        const pdfBlob = pdf.output("blob");
        resolve(pdfBlob);
      } catch (err) {
        reject(err);
      }
    });
  }

  reject(error: any): void {}

  teethList = [
    18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28, 48, 47, 46,
    45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
  ];

  showBridge(toothIdA: number, toothIdB: number): boolean {
    var returnVal = false;

    this.orderDetailsNew.bridges.forEach((bridge) => {
      var bridgeStarted = false;
      var bridgeEnded = false;
      this.teethList.forEach((teeth) => {
        if (teeth == bridge.start) {
          bridgeStarted = true;
        }

        if (teeth == bridge.end) {
          bridgeEnded = true;
        }

        if (bridgeStarted && !bridgeEnded) {
          if (teeth == toothIdA) {
            returnVal = true;
          }
        }
      });
    });

    return returnVal;
  }

  showBridgeDot(toothId: number): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.teeth == toothId && data.bridgeConnectionType != "",
    );
  }

  get anteriorPonticTeeth(): number[] {
    return this.dataService.anteriorPonticTeeth(this.orderDetailsNew);
  }

  get posteriorPonticTeeth(): number[] {
    return this.dataService.posteriorPonticTeeth(this.orderDetailsNew);
  }

  anteriorBridgedTeeth(bridgedTeeth: number[]): number[] {
    return this.dataService.anteriorBridgedTeeth(
      this.orderDetailsNew,
      bridgedTeeth,
    );
  }

  posteriorBridgedTeeth(bridgedTeeth: number[]): number[] {
    return this.dataService.posteriorBridgedTeeth(
      this.orderDetailsNew,
      bridgedTeeth,
    );
  }

  get anteriorTeethForFrame_1(): number[] {
    return this.dataService.anteriorTeethForFrame_1(this.orderDetailsNew);
  }

  get posteriorTeethForFrame_1(): number[] {
    return this.dataService.posteriorTeethForFrame_1(this.orderDetailsNew);
  }

  get anteriorBridgedTeethFrame_1(): number[] {
    var bridgedTeeth = this.orderDetailsNew.bridges
      .map((b) => b.bridgedTeeth)
      .flat();
    if (
      bridgedTeeth.some((teeth) =>
        this.orderDetailsNew.toothData.some(
          (data) => data.teeth == teeth && data.type == "Frame",
        ),
      )
    ) {
      return this.dataService.groupByAnteriorAndPosterior_1(bridgedTeeth)
        .anterior;
    } else {
      return [];
    }

    // return this.dataService.groupByAnteriorAndPosterior(this.orderDetailsNew.bridges.map(b => b.bridgedTeeth).flat()).anterior;
  }

  get posteriorBridgedTeethFrame_1(): number[] {
    var bridgedTeeth = this.orderDetailsNew.bridges
      .map((b) => b.bridgedTeeth)
      .flat();
    if (
      bridgedTeeth.some((teeth) =>
        this.orderDetailsNew.toothData.some(
          (data) => data.teeth == teeth && data.type == "Frame",
        ),
      )
    ) {
      return this.dataService.groupByAnteriorAndPosterior_1(bridgedTeeth)
        .posterior;
    } else {
      return [];
    }

    // return this.dataService.groupByAnteriorAndPosterior(this.orderDetailsNew.bridges.map(b => b.bridgedTeeth).flat()).posterior;
  }

  anteriorBridgedTeethFrame(bridgedTeeth: number[]): number[] {
    return this.dataService.anteriorBridgedTeethFrame(
      this.orderDetailsNew,
      bridgedTeeth,
    );
  }

  posteriorBridgedTeethFrame(bridgedTeeth: number[]): number[] {
    return this.dataService.posteriorBridgedTeethFrame(
      this.orderDetailsNew,
      bridgedTeeth,
    );
  }

  get bridgedTeethFrame(): number[] {
    var bridgedTeeth = this.orderDetailsNew.bridges
      .map((b) => b.bridgedTeeth)
      .flat();
    if (
      bridgedTeeth.some((teeth) =>
        this.orderDetailsNew.toothData.some(
          (data) => data.teeth == teeth && data.type == "Frame",
        ),
      )
    ) {
      return bridgedTeeth;
    } else {
      return [];
    }
  }

  get isFrameCrownSelected(): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.subType == "Crown" && data.type == "Frame",
    );
  }
  get isFramePonticSelected(): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.subType == "Pontic" && data.type == "Frame",
    );
  }
  get isFrameInlayOnlaySelected(): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.subType == "Inlay/Onlay" && data.type == "Frame",
    );
  }
  get isFrameVeneerSelected(): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.subType == "Veneer" && data.type == "Frame",
    );
  }

  get isAnatomyCrownSelected(): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.subType == "Crown" && data.type == "Anatomy",
    );
  }
  get isAnatomyPonticSelected(): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.subType == "Pontic" && data.type == "Anatomy",
    );
  }
  get isAnatomyInlayOnlaySelected(): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.subType == "Inlay/Onlay" && data.type == "Anatomy",
    );
  }
  get isAnatomyVeneerSelected(): boolean {
    return this.orderDetailsNew.toothData.some(
      (data) => data.subType == "Veneer" && data.type == "Anatomy",
    );
  }

  get isBridgeSelected(): boolean {
    return this.orderDetailsNew.bridges.length > 0;
  }

  articulationDetailsPropsDefaultValues: any = {
    "Protrusion Angle": "30",
    "Bennett Angle": "15",
    "Immediate Side Shift (ISS)": "0.5",
    "Incisal Guidance": "10",
    "Raising or lowering the articulator": "-0.2",
  };

  get articulationDetailsWithNonDefaultValues(): any[] {
    var articulationDetailsWithNonDefaultValues: any[] = [];

    Object.keys(this.orderDetailsNew.articulationDetails.props).forEach(
      (key) => {
        if (
          this.orderDetailsNew.articulationDetails.props[key] !=
          this.articulationDetailsPropsDefaultValues[key]
        ) {
          articulationDetailsWithNonDefaultValues.push({
            key: key,
            value: this.orderDetailsNew.articulationDetails.props[key],
          });
        }
      },
    );

    return articulationDetailsWithNonDefaultValues;
  }

  get articulationDetailsWithDefaultValues(): any[] {
    var articulationDetailsWithDefaultValues: any[] = [];

    Object.keys(this.orderDetailsNew.articulationDetails.props).forEach(
      (key) => {
        if (
          this.orderDetailsNew.articulationDetails.props[key] ==
          this.articulationDetailsPropsDefaultValues[key]
        ) {
          articulationDetailsWithDefaultValues.push({
            key: key,
            value: this.orderDetailsNew.articulationDetails.props[key],
          });
        }
      },
    );

    return articulationDetailsWithDefaultValues;
  }

  updateTaskacceptance(data: string) {
    this.dataService.updateProgressBarSubject(true);
    this.dataService.post("/tasks/" + this.taskId + "/" + data, {}).subscribe({
      //   next: (response) => {
      //     // this.showSliderComments.value = true;
      //     if(this.notificationType == 'comment'){
      //       this.openCommentModal();
      //     }
      //   },
      //   error: (err) => {
      //     // this.dialogService.handleError(err, 'Failed to Complete task, Please try again.');
      //   }
      //
    });
  }

  get calculateAmountOnTimeAndTier(): number {
    let amount = 0.0;

    if (this.orderDetailsNew.showAi) {
      if (this.orderDetailsNew.deliveryTime == "10") {
        amount = 3.87;
      }

      if (this.orderDetailsNew.deliveryTime == "4") {
        amount = 4.78;
      }
    }

    if (this.orderDetailsNew.tier == "silver") {
      amount = 6.99;
    }

    if (this.orderDetailsNew.tier == "gold") {
      amount = 12.99;
      if (this.orderDetailsNew.deliveryTime === "12") {
        amount += 2.99;
      }
    }

    if (this.orderDetailsNew.tier == "platinum") {
      amount = 31.99;
      if (this.orderDetailsNew.deliveryTime === "12") {
        amount += 2.99;
      }
      if (this.orderDetailsNew.deliveryTime === "8") {
        amount += 4.99;
      }
    }

    return amount * this.orderDetailsNew.toothData.length; // Multiply by number of teeth selected
  }

  get getOrderAmount(): number {
    if (this.orderDetailsNew && this.orderDetailsNew.amount) {
      return this.orderDetailsNew.amount;
    } else {
      return this.calculateAmountOnTimeAndTier;
    }
    return 0;
  }

  submitReview(TaskId: number) {
    this.dataService.updateProgressBarSubject(true);
    this.dataService
      .post("/common/notifications/" + TaskId + "/IR", {})
      .subscribe({
        next: (response) => {
          this.dataService.updateProgressBarSubject(false);
          this.dialogService.openMessageDialog(
            "Review Submitted",
            "Review submitted successfully.",
          );
          this.home.fetchNotifications();
          this.router.navigate(["task-list/all-tasks"]);
        },
        error: (err) => {
          this.dialogService.handleError(
            err,
            "Failed to submit review, Please try again.",
          );
        },
      });
  }
  closePopup(): void {
    const statusParam = this.selectedStatuses?.join(",") || "";

    // save filters so refresh in details doesn't lose them
    const filters = this.dataService.getSelectedTaskFilters();
    sessionStorage.setItem("selectedTaskFilters", JSON.stringify(filters));

    this.router
      .navigate(["/task-list", "all-tasks"], {
        queryParams: {
          page: this.currentPage,
          pageSize: this.pageSizes,
          status: statusParam,
        },
        state: { fromDetails: true },
      })
      .then(() => {
        this.dataService.trigger("");
      });
  }
}
